import { createContext, useContext, ReactNode, useEffect } from 'react';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useAuthStore } from '@/stores/auth';
import type { Socket } from 'socket.io-client';

interface WebSocketMessage {
  type: 'incident' | 'threat' | 'alert' | 'notification' | 'status_update';
  id: string;
  title: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  data?: Record<string, unknown>;
}

interface WebSocketContextType {
  isConnected: boolean;
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastMessage: WebSocketMessage | null;
  messages: WebSocketMessage[];
  connect: () => void;
  disconnect: () => void;
  send: (event: string, data: unknown) => boolean;
  clearMessages: () => void;
  socket: Socket | null;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const websocket = useWebSocket({
    // url will default to import.meta.env.VITE_WS_URL || 'http://localhost:3000'
    autoConnect: false, // We'll manage connection based on auth state
    reconnectAttempts: 5,
    reconnectDelay: 3000,
    showToastNotifications: true,
  });

  // Connect/disconnect based on authentication state
  useEffect(() => {
    if (isAuthenticated) {
      websocket.connect();
    } else {
      websocket.disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- websocket methods are stable, only react to auth changes
  }, [isAuthenticated]);

  return (
    <WebSocketContext.Provider value={websocket}>
      {children}
    </WebSocketContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useWebSocketContext() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocketContext must be used within a WebSocketProvider');
  }
  return context;
}
